import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import type { SupplyItem, SupplyLocation } from '@teo/shared';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { Badge, Panel } from '../components/Panel';
import { Modal } from '../components/Overlay';
import { EmptyState, TableSkeleton } from '../components/States';
import { useLoad, useSubmit } from '../lib/useAsync';
import { useToast } from '../lib/useToast';
import { releaseKey, requestKey } from '../lib/idempotency';
import { formatNumber } from '../lib/format';
import type { Language } from '../i18n';

const TONE = { GREEN: 'green', AMBER: 'amber', RED: 'red' } as const;

/** Vista "Abastecimiento": semáforo por local y, al abrir uno, qué referencias necesitan reposición (Etapa 2). */
export function Supply() {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage as Language;
  const { user, can } = useAuth();
  const { show, showError } = useToast();
  const { busy, run } = useSubmit();
  const places = user?.locations ?? [];

  const [selected, setSelected] = useState<number | null>(null);
  // Quien solo tiene una ubicación entra directo a su semáforo (el usuario llega después del primer render).
  const locationId = selected ?? (places.length === 1 ? (places[0]?.id ?? null) : null);
  const [onlyAlerts, setOnlyAlerts] = useState(true);
  // El resumen de todas las ubicaciones se mantiene visible aunque se abra una: son dos consultas distintas.
  const supply = useLoad(() => (user ? api.supply({ only_alerts: true }) : Promise.resolve(null)), [user]);
  const detail = useLoad(
    () => (user && locationId != null ? api.supply({ location_id: locationId, only_alerts: onlyAlerts }) : Promise.resolve(null)),
    [user, locationId, onlyAlerts],
  );
  const reload = () => Promise.all([supply.reload(), detail.reload()]);

  const [rulesOpen, setRulesOpen] = useState(false);
  const [rules, setRules] = useState({ restock_factor: '', amber_pct: '', red_pct: '' });
  const [minFor, setMinFor] = useState<SupplyItem | null>(null);
  const [minValue, setMinValue] = useState('');

  const locations = supply.data?.locations ?? [];
  const items = detail.data?.items ?? [];
  const current = locations.find((l) => l.location_id === locationId) ?? null;
  const settings = supply.data?.settings;

  const openRules = () => {
    if (!settings) return;
    setRules({ restock_factor: String(settings.restock_factor), amber_pct: String(settings.amber_pct), red_pct: String(settings.red_pct) });
    setRulesOpen(true);
  };

  const saveRules = (e: FormEvent) => {
    e.preventDefault();
    void run(async () => {
      try {
        await api.saveSupplySettings(requestKey('abasto:reglas'), {
          restock_factor: Number(rules.restock_factor), amber_pct: Number(rules.amber_pct), red_pct: Number(rules.red_pct),
        });
        releaseKey('abasto:reglas');
        setRulesOpen(false);
        show(t('supply.rulesSaved'));
        await reload();
      } catch (err) { showError(err); }
    });
  };

  const saveMinimum = (e: FormEvent) => {
    e.preventDefault();
    if (!minFor || locationId == null) return;
    const scope = `abasto:minimo:${locationId}:${minFor.product_id}`;
    void run(async () => {
      try {
        await api.saveMinimum(requestKey(scope), { location_id: locationId, product_id: minFor.product_id, min_stock: minValue.trim() === '' ? null : Number(minValue) });
        releaseKey(scope);
        setMinFor(null); setMinValue('');
        show(t('supply.minSaved'));
        await reload();
      } catch (err) { showError(err); }
    });
  };

  const card = (l: SupplyLocation) => (
    <button
      type="button"
      key={l.location_id}
      className={`kpi supplycard${locationId === l.location_id ? ' active' : ''}`}
      data-location={l.code}
      data-status={l.status}
      onClick={() => setSelected(l.location_id)}
    >
      <div className="lab">
        {l.name} <Badge tone={TONE[l.status]}>{t(`supply.status.${l.status}`)}</Badge>
      </div>
      <div className="val">{t('supply.refs', { n: formatNumber(l.refs, lang) })}</div>
      <div className="sub">
        {[
          t('supply.outOfStock', { n: formatNumber(l.out_of_stock, lang) }),
          t('supply.restock', { n: formatNumber(l.restock, lang) }),
          t('supply.transit', { n: formatNumber(l.incoming_transit, lang) }),
        ].join(' · ')}
      </div>
    </button>
  );

  return (
    <>
      <div className="actions" style={{ marginBottom: 10, justifyContent: 'space-between' }}>
        <span className="help" id="supplyLegend">{t('supply.legend')}</span>
        {can('config.manage') ? (
          <button type="button" className="btn" id="supplyRules" onClick={openRules} disabled={!settings}>
            {t('supply.rules')}
          </button>
        ) : null}
      </div>

      <div className="kpis" id="supplyLocations">
        {supply.loading && !supply.data ? <div className="kpi" aria-busy="true" /> : null}
        {locations.map(card)}
      </div>
      {supply.data && locations.length === 0 ? <EmptyState icon="inbox" title={t('supply.noLocations')} /> : null}
      {settings ? (
        <div className="help" id="supplyCurrentRules" style={{ marginTop: 8 }}>
          {t('supply.currentRules', { factor: settings.restock_factor, amber: settings.amber_pct, red: settings.red_pct })}
        </div>
      ) : null}

      {current ? (
        <div style={{ marginTop: 14 }}>
          <Panel
            title={t('supply.detailTitle', { location: current.name })}
            description={t('supply.detailDesc')}
            badge={
              <label className="help" htmlFor="supplyOnlyAlerts" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input id="supplyOnlyAlerts" type="checkbox" checked={onlyAlerts} onChange={(e) => setOnlyAlerts(e.target.checked)} />
                {t('supply.onlyAlerts')}
              </label>
            }
          >
            {detail.data && items.length === 0 ? (
              <EmptyState icon="inbox" title={t('supply.allGood')} />
            ) : (
              <div className="tablewrap">
                <table>
                  <thead>
                    <tr>
                      <th>{t('supply.cols.ref')}</th>
                      <th>{t('supply.cols.product')}</th>
                      <th className="num">{t('supply.cols.available')}</th>
                      <th className="num">{t('supply.cols.min')}</th>
                      <th className="num">{t('supply.cols.transit')}</th>
                      <th>{t('supply.cols.status')}</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody id="supplyBody">
                    {detail.loading && !detail.data ? <TableSkeleton cols={7} rows={4} /> : null}
                    {items.map((i) => (
                      <tr key={i.product_id} data-ref={i.ref} data-status={i.status}>
                        <td><b>{i.ref}</b></td>
                        <td>{i.product_name}</td>
                        <td className="num" data-col="qty">{formatNumber(i.qty, lang)}</td>
                        <td className="num" data-col="min">
                          {formatNumber(i.min_stock, lang)}
                          <div className="tiny muted">{t(i.own_minimum ? 'supply.ownMinimum' : 'supply.generalMinimum')}</div>
                        </td>
                        <td className="num" data-col="transit">{formatNumber(i.incoming_transit, lang)}</td>
                        <td><Badge tone={TONE[i.status]}>{t(`supply.status.${i.status}`)}</Badge></td>
                        <td>
                          {can('config.manage') ? (
                            <button type="button" className="btn sm" data-min={i.ref} onClick={() => { setMinFor(i); setMinValue(i.own_minimum ? String(i.min_stock) : ''); }}>
                              {t('supply.editMin')}
                            </button>
                          ) : null}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Panel>
        </div>
      ) : null}

      <Modal open={rulesOpen} title={t('supply.rulesTitle')} help={t('supply.rulesHelp')} onClose={() => setRulesOpen(false)}>
        <form className="formgrid" id="supplyRulesForm" onSubmit={saveRules}>
          <div className="field span2">
            <label htmlFor="srFactor">{t('supply.restockFactor')}</label>
            <input id="srFactor" type="number" step="0.1" min={0.1} max={5} required value={rules.restock_factor} onChange={(e) => setRules({ ...rules, restock_factor: e.target.value })} />
          </div>
          <div className="field">
            <label htmlFor="srAmber">{t('supply.amberPct')}</label>
            <input id="srAmber" type="number" min={0} max={100} required value={rules.amber_pct} onChange={(e) => setRules({ ...rules, amber_pct: e.target.value })} />
          </div>
          <div className="field">
            <label htmlFor="srRed">{t('supply.redPct')}</label>
            <input id="srRed" type="number" min={0} max={100} required value={rules.red_pct} onChange={(e) => setRules({ ...rules, red_pct: e.target.value })} />
          </div>
          <button className="btn black span2" type="submit" id="srSubmit" disabled={busy}>{t('common.save')}</button>
        </form>
      </Modal>

      <Modal
        open={!!minFor}
        title={t('supply.minTitle', { ref: minFor?.ref ?? '', location: current?.name ?? '' })}
        help={t('supply.minHelp')}
        onClose={() => setMinFor(null)}
      >
        <form className="formgrid" id="supplyMinForm" onSubmit={saveMinimum}>
          <div className="field span2">
            <label htmlFor="smValue">{t('supply.minValue')}</label>
            <input id="smValue" type="number" min={0} value={minValue} onChange={(e) => setMinValue(e.target.value)} />
          </div>
          <button className="btn black" type="submit" id="smSubmit" disabled={busy}>{t('common.save')}</button>
          <button type="button" className="btn" id="smGeneral" disabled={busy} onClick={() => { setMinValue(''); }}>
            {t('supply.useGeneral')}
          </button>
        </form>
      </Modal>
    </>
  );
}
